import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/global/Layout';
import ErrorBoundary from './components/global/ErrorBoundary';
import { trackPageView, Analytics as GA } from './utils/analytics';
import CookieConsent from './components/global/CookieConsent';
import PWAInstallBanner from './components/pwa/PWAInstallBanner';
import OfflineIndicator from './components/pwa/OfflineIndicator';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';

// Auth pages (LoginPage has its own layout)
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const AccountPage = lazy(() => import('./pages/account/AccountPage'));
const PremiumReadingPage = lazy(() => import('./pages/account/PremiumReadingPage'));
const PremiumAdminPage = lazy(() => import('./pages/admin/PremiumAdminPage'));
const ContactPage = lazy(() => import('./pages/contact/ContactPage'));

// Scroll to top + GA4 page view tracking on route change
function ScrollToTop() {
    const { pathname, search } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
        trackPageView(pathname);
    }, [pathname]);
    // A landing tagged ?src=push|email means the user came back via a
    // notification or email link — the return half of the retention loop.
    useEffect(() => {
        const src = new URLSearchParams(search).get('src');
        if (src === 'push' || src === 'email') {
            GA.notifReturnVisit(src);
        }
    }, [search]);
    return null;
}

// Loading component for Suspense fallback
const PageLoader = () => (
    <div className="min-h-screen bg-gradient-to-b from-cosmic-900 via-cosmic to-cosmic-900 flex items-center justify-center">
        <div className="text-center">
            <div className="w-16 h-16 border-4 border-divine border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-divine-200 font-noto-serif">Loading sacred content...</p>
        </div>
    </div>
);

// Lazy load all pages for code splitting
const Home = lazy(() => import('./pages/Home'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));

// Bhairav pages - lazy loaded
const BhairavPage = lazy(() => import('./pages/bhairav/BhairavPage'));
const FormsPage = lazy(() => import('./pages/bhairav/FormsPage'));
const FormDetailPage = lazy(() => import('./pages/bhairav/FormDetailPage'));
const BhairavaTreePage = lazy(() => import('./pages/bhairav/BhairavaTreePage'));
const StotrasPage = lazy(() => import('./pages/bhairav/StotrasPage'));
const StotraDetailPage = lazy(() => import('./pages/bhairav/StotraDetailPage'));
const ShivPage = lazy(() => import('./pages/shiv/ShivPage'));
const ShivStotrasPage = lazy(() => import('./pages/shiv/ShivStotrasPage'));
const ShivStotraDetailPage = lazy(() => import('./pages/shiv/ShivStotraDetailPage'));
const RudrashtadhyayiPage = lazy(() => import('./pages/shiv/RudrashtadhyayiPage'));
const RudrashtadhyayiDetailPage = lazy(() => import('./pages/shiv/RudrashtadhyayiDetailPage'));
const SivaSutrasPage = lazy(() => import('./pages/shiv/SivaSutrasPage'));
const SivaSutraDetailPage = lazy(() => import('./pages/shiv/SivaSutraDetailPage'));
const SpandaKarikasPage = lazy(() => import('./pages/shiv/SpandaKarikasPage'));
const SpandaKarikaDetailPage = lazy(() => import('./pages/shiv/SpandaKarikaDetailPage'));
const IsvaraGitaPage = lazy(() => import('./pages/shiv/IsvaraGitaPage'));
const IsvaraGitaChapterPage = lazy(() => import('./pages/shiv/IsvaraGitaChapterPage'));
const ShivaPuranaPage = lazy(() => import('./pages/shiv/ShivaPuranaPage'));
const ShivFormsPage = lazy(() => import('./pages/shiv/ShivFormsPage'));
const ShivSampradayaListingPage = lazy(() => import('./pages/shiv/ShivSampradayaListingPage'));
const ShivSampradayaDetailPage = lazy(() => import('./pages/shiv/ShivSampradayaDetailPage'));
const ShivLingaSymbolismPage = lazy(() => import('./pages/shiv/ShivLingaSymbolismPage'));
const ShivTopicDetailPage = lazy(() => import('./pages/shiv/ShivTopicDetailPage'));
const ShivTopicsListingPage = lazy(() => import('./pages/shiv/ShivTopicsListingPage'));
const ShivJyotirlingasPage = lazy(() => import('./pages/shiv/ShivJyotirlingasPage'));
const ShivJyotirlingaDetailPage = lazy(() => import('./pages/shiv/ShivJyotirlingaDetailPage'));
const ShivaPuranaSamhitaPage = lazy(() => import('./pages/shiv/ShivaPuranaSamhitaPage'));
const ShivaPuranaChapterPage = lazy(() => import('./pages/shiv/ShivaPuranaChapterPage'));
const ShivVidhiListingPage = lazy(() => import('./pages/shiv/ShivVidhiListingPage'));
const ShivVidhiPage = lazy(() => import('./pages/shiv/ShivVidhiPage'));
const ArticlesPage = lazy(() => import('./pages/bhairav/ArticlesPage'));
const ArticleDetailPage = lazy(() => import('./pages/bhairav/ArticleDetailPage'));
const NaamJapaListingPage = lazy(() => import('./pages/bhairav/NaamJapaListingPage'));
const NaamJapaCounterPage = lazy(() => import('./pages/bhairav/NaamJapaCounterPage'));
const NaamJapaGuidePage = lazy(() => import('./pages/bhairav/NaamJapaGuidePage'));
const VidhiListingPage = lazy(() => import('./pages/bhairav/VidhiListingPage'));
const VidhiPage = lazy(() => import('./pages/bhairav/VidhiPage'));
const ShastraStoriesPage = lazy(() => import('./pages/bhairav/ShastraStoriesPage'));
const StoryDetailPage = lazy(() => import('./pages/bhairav/StoryDetailPage'));
const TattvaPage = lazy(() => import('./pages/bhairav/TattvaPage'));
const TattvaArticleDetailPage = lazy(() => import('./pages/bhairav/TattvaArticleDetailPage'));
const LifePurposeLandingPage = lazy(() => import('./pages/bhairav/LifePurposeLandingPage'));
const LifePurposeQuestionnairePage = lazy(() => import('./pages/bhairav/LifePurposeQuestionnairePage'));
const LifePurposeResultsPage = lazy(() => import('./pages/bhairav/LifePurposeResultsPage'));
const LifePurposePaymentPage = lazy(() => import('./pages/bhairav/LifePurposePaymentPage'));
const SadhanaPage = lazy(() => import('./pages/bhairav/SadhanaPage'));
const BooksPage = lazy(() => import('./pages/bhairav/BooksPage'));
const PilgrimagePlacesPage = lazy(() => import('./pages/bhairav/PilgrimagePlacesPage'));
const VBTListingPage = lazy(() => import('./pages/bhairav/VBTListingPage'));
const VBTTechniquePage = lazy(() => import('./pages/bhairav/VBTTechniquePage'));

// Quiz pages
const QuizPage = lazy(() => import('./pages/quiz/QuizPage'));
const QuizResultPage = lazy(() => import('./pages/quiz/QuizResultPage'));

// About page
const AboutPage = lazy(() => import('./pages/AboutPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));

// Resources & Films
const ResourcesPage = lazy(() => import('./pages/resources/ResourcesPage'));
const FilmsPage = lazy(() => import('./pages/films/FilmsPage'));
const FilmWatchPage = lazy(() => import('./pages/films/FilmWatchPage'));

// 404 page
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Legal pages
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));

export default function App() {
    return (
        <HelmetProvider>
            <Router>
                <ScrollToTop />
                <SpeedInsights />
                <Analytics />
                <CookieConsent />
                <PWAInstallBanner />
                <OfflineIndicator />
                <AuthProvider>
                    <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                            <Routes>
                                {/* Login Page - No standard layout */}
                                <Route path="/login" element={<LoginPage />} />

                                {/* All other pages with standard layout */}
                                <Route path="/*" element={
                                    <Layout>
                                        <Routes>
                                            <Route path="/" element={<Home />} />
                                            <Route path="/search" element={<SearchPage />} />

                                            {/* Auth & Account Routes */}
                                            <Route path="/account" element={<AccountPage />} />
                                            <Route path="/account/reading" element={<PremiumReadingPage />} />
                                            <Route path="/admin/premium" element={<PremiumAdminPage />} />
                                            <Route path="/contact" element={<ContactPage />} />

                                            {/* Bhairav Section */}
                                            <Route path="/bhairav" element={<BhairavPage />} />
                                            <Route path="/bhairav/forms" element={<FormsPage />} />
                                            <Route path="/bhairav/bhairava-tree" element={<BhairavaTreePage />} />
                                            <Route path="/bhairav/forms/:slug" element={<FormDetailPage />} />
                                            <Route path="/bhairav/tattva" element={<TattvaPage />} />
                                            <Route path="/bhairav/tattva/:slug" element={<TattvaArticleDetailPage />} />
                                            <Route path="/bhairav/stotras" element={<StotrasPage />} />
                                            <Route path="/bhairav/stotras/:slug" element={<StotraDetailPage />} />
                                            <Route path="/bhairav/naam-japa" element={<NaamJapaListingPage />} />
                                            <Route path="/bhairav/naam-japa-counter/:formSlug" element={<NaamJapaCounterPage />} />
                                            <Route path="/bhairav/naam-japa-guide/:slug" element={<NaamJapaGuidePage />} />
                                            <Route path="/bhairav/vidhi" element={<VidhiListingPage />} />
                                            <Route path="/bhairav/vidhi/:slug" element={<VidhiPage />} />
                                            <Route path="/bhairav/articles" element={<ArticlesPage />} />
                                            <Route path="/bhairav/articles/:slug" element={<ArticleDetailPage />} />
                                            <Route path="/bhairav/life-purpose" element={<LifePurposeLandingPage />} />
                                            <Route path="/bhairav/life-purpose/questionnaire" element={<LifePurposeQuestionnairePage />} />
                                            <Route path="/bhairav/life-purpose/payment" element={<LifePurposePaymentPage />} />
                                            <Route path="/bhairav/life-purpose/results" element={<LifePurposeResultsPage />} />
                                            <Route path="/bhairav/images" element={<ComingSoon />} />
                                            <Route path="/bhairav/videos" element={<ComingSoon />} />
                                            <Route path="/bhairav/shastra-stories" element={<ShastraStoriesPage />} />
                                            <Route path="/bhairav/shastra-stories/:slug" element={<StoryDetailPage />} />
                                            <Route path="/bhairav/sadhana" element={<SadhanaPage />} />
                                            <Route path="/bhairav/pilgrimage-places" element={<PilgrimagePlacesPage />} />
                                            <Route path="/bhairav/books" element={<BooksPage />} />
                                            <Route path="/bhairav/vbt" element={<VBTListingPage />} />
                                            <Route path="/bhairav/vbt/:slug" element={<VBTTechniquePage />} />

                                            {/* Bhairava Quiz */}
                                            <Route path="/quiz" element={<QuizPage />} />
                                            <Route path="/quiz/result/:formSlug" element={<QuizResultPage />} />

                                            {/* Other Deity Sections */}
                                            <Route path="/shiv" element={<ShivPage />} />
                                            <Route path="/shiv/stotras" element={<ShivStotrasPage />} />
                                            <Route path="/shiv/stotras/:slug" element={<ShivStotraDetailPage />} />
                                            <Route path="/shiv/rudrashtadhyayi" element={<RudrashtadhyayiPage />} />
                                            <Route path="/shiv/rudrashtadhyayi/:slug" element={<RudrashtadhyayiDetailPage />} />
                                            <Route path="/shiv/siva-sutras" element={<SivaSutrasPage />} />
                                            <Route path="/shiv/siva-sutras/:slug" element={<SivaSutraDetailPage />} />
                                            <Route path="/shiv/spanda-karikas" element={<SpandaKarikasPage />} />
                                            <Route path="/shiv/spanda-karikas/:slug" element={<SpandaKarikaDetailPage />} />
                                            <Route path="/shiv/isvara-gita" element={<IsvaraGitaPage />} />
                                            <Route path="/shiv/isvara-gita/:slug" element={<IsvaraGitaChapterPage />} />
                                            <Route path="/shiv/shiva-purana" element={<ShivaPuranaPage />} />
                                            <Route path="/shiv/forms" element={<ShivFormsPage />} />
                                            <Route path="/shiv/sampradaya" element={<ShivSampradayaListingPage />} />
                                            <Route path="/shiv/sampradaya/:slug" element={<ShivSampradayaDetailPage />} />
                                            <Route path="/shiv/linga-symbolism" element={<ShivLingaSymbolismPage />} />
                                            <Route path="/shiv/topics" element={<ShivTopicsListingPage />} />
                                            <Route path="/shiv/topics/:slug" element={<ShivTopicDetailPage />} />
                                            <Route path="/shiv/jyotirlingas" element={<ShivJyotirlingasPage />} />
                                            <Route path="/shiv/jyotirlingas/:slug" element={<ShivJyotirlingaDetailPage />} />
                                            <Route path="/shiv/shiva-purana/:samhitaSlug" element={<ShivaPuranaSamhitaPage />} />
                                            <Route path="/shiv/shiva-purana/:samhitaSlug/:chapterSlug" element={<ShivaPuranaChapterPage />} />
                                            <Route path="/shiv/vidhi" element={<ShivVidhiListingPage />} />
                                            <Route path="/shiv/vidhi/:slug" element={<ShivVidhiPage />} />
                                            <Route path="/shiv/*" element={<ComingSoon />} />
                                            <Route path="/maa/*" element={<ComingSoon />} />
                                            <Route path="/guru/*" element={<ComingSoon />} />
                                            <Route path="/krishna/*" element={<ComingSoon />} />
                                            <Route path="/hanuman/*" element={<ComingSoon />} />
                                            <Route path="/vishnu/*" element={<ComingSoon />} />
                                            <Route path="/about" element={<AboutPage />} />
                                            <Route path="/support" element={<SupportPage />} />
                                            <Route path="/donate" element={<Navigate to="/support" replace />} />

                                            {/* Films */}
                                            <Route path="/films" element={<FilmsPage />} />
                                            <Route path="/films/:slug" element={<FilmWatchPage />} />

                                            {/* Legal Pages */}
                                            <Route path="/terms" element={<TermsOfServicePage />} />
                                            <Route path="/privacy" element={<PrivacyPolicyPage />} />

                                            {/* General and Resources */}
                                            <Route path="/general/*" element={<ComingSoon />} />
                                            <Route path="/resources" element={<ResourcesPage />} />
                                            <Route path="/creations/*" element={<ComingSoon />} />

                                            {/* 404 Catch-all */}
                                            <Route path="*" element={<NotFoundPage />} />
                                        </Routes>
                                    </Layout>
                                } />
                            </Routes>
                        </Suspense>
                    </ErrorBoundary>
                </AuthProvider>
            </Router>
        </HelmetProvider>
    );
}
